import type { UseFormPayload } from '@domain-group/react-hooks'
import { useSetState } from '@domain-group/react-hooks'

import useBrandGroupProvider from '@hooks/useBrandGroupProvider'
import useBrandGroupPublication, { useBrandGroupPublicationForm } from '@hooks/useBrandGroupPublication'
import useBrandGroups from '@hooks/useBrandGroups'
import useBrandProvider from '@hooks/useBrandProvider'

import type { BrandModel, BrandGroupPublicationModel } from '@models/types'

type DefaultState = {
  brandId?: number,
}

type SetState = (state: Partial<DefaultState>) => void

type HandleBrandSelectorParams = {
  brand?: BrandModel,
  setState: SetState,
  setEntityState: UseFormPayload['setEntityState'],
}

const handleBrandSelector = (params: HandleBrandSelectorParams) => {
  const { brand, setState, setEntityState } = params

  if (!brand){
    setEntityState({ brand_group_id: undefined })
    // @ts-expect-error TS2322
    setState({ brandId: null })
    return
  }

  setState({ brandId: brand.id })
}

const defaultState: DefaultState = {
  brandId: undefined,
}

interface CreatePublicationPaginationRuleModalParams {
  brandGroupPublication: BrandGroupPublicationModel,
  closeModal: () => void,
}

const useCreateBrandGroupPublicationModal = (params: CreatePublicationPaginationRuleModalParams) => {
  const { closeModal, brandGroupPublication } = params

  const [state, setState] = useSetState(defaultState)
  const { brandId } = state

  const {
    brandGroup,
    callbacks: { createBrandGroupPublication: createFn },
    creating,
    publication,
  } = useBrandGroupPublication(brandGroupPublication)

  const brandGroupPublicationFormPayload = useBrandGroupPublicationForm(brandGroupPublication, { validateOn: 'change' })
  const { entityState, setEntityState } = brandGroupPublicationFormPayload

  const brandGroupsPayload = useBrandGroups({
    filters: {
      brand_id: brandId,
    },
    performHttpRequests: !!publication?.id && !!brandId,
    requestOptions: {
      include_brand_group_provider_ids: true,
    },
    skipLoadedForKeysCheck: true,
  })

  const {
    filteredBrandGroups,
  } = brandGroupsPayload

  const {
    callbacks: {
      addBrandGroupProvider,
    },
  } = useBrandGroupProvider({ brand_group_id: brandGroupPublication.brand_group_id })

  const {
    callbacks: { addBrandProvider },
  } = useBrandProvider({ provider_id: publication.provider_id })

  const entityText = brandGroupPublication.brand_group_id ? 'Publication' : 'Brand Group'

  const createBrandGroupPublicationPayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `${entityText} Added`,
  }

  return (
    {
      callbacks: {
        addBrandGroupProvider,
        addBrandProvider,
        handleBrandSelector: (brand?: BrandModel) => handleBrandSelector({ brand, setState, setEntityState }),
      },
      brandGroup,
      brandGroupPublicationFormPayload,
      brandId,
      createBrandGroupPublicationPayload,
      creating,
      entityText,
      filteredBrandGroups,
      publication,
    }
  )
}

export default useCreateBrandGroupPublicationModal
